import React, { useState, useMemo } from 'react';
import { Box, Grid, Card, CardContent, Typography, FormControlLabel, Switch } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import Skeleton from 'react-loading-skeleton';

const ClientAnalytics = ({ clientData, isLoading }) => {
  const [includeZeroBalance, setIncludeZeroBalance] = useState(true);
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const columns = [
    {
      field: 'rank',
      headerName: '#',
      width: 60,
      headerClassName: 'super-app-theme--header'
    },
    {
      field: 'name',
      headerName: 'Client Name',
      width: 250,
      headerClassName: 'super-app-theme--header'
    },
    {
      field: 'currentBalance',
      headerName: 'Current Balance',
      width: 180,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'totalPerformance',
      headerName: 'Total Performance (Net)',
      width: 200,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'nOfDeposits',
      headerName: 'N D',
      width: 70,
      headerClassName: 'super-app-theme--header',
      description: 'Number of approved deposits'
    },
    {
      field: 'totalDeposits',
      headerName: 'Total Deposits',
      width: 180,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'nOfWithdrawals',
      headerName: 'N W',
      width: 70,
      headerClassName: 'super-app-theme--header',
      description: 'Number of paid withdrawals'
    },
    {
      field: 'totalWithdrawals',
      headerName: 'Total Withdrawals',
      width: 180,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'clientsOnlyFund',
      headerName: 'Clients Only Fund',
      width: 200,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'monthsActive',
      headerName: 'Months Active',
      width: 130,
      headerClassName: 'super-app-theme--header'
    },
    {
      field: 'accountOpenDate',
      headerName: 'Account Open Date',
      width: 150,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => params.value
        ? new Date(params.value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—',
      description: 'From first ledger in database (not from contracts)'
    }
  ];

  // All hooks must run unconditionally (before any early return)
  const allClientsSorted = useMemo(() => {
    const list = clientData?.allClients || [];
    return list.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }, [clientData?.allClients]);

  const allRows = useMemo(() => allClientsSorted.map((client, index) => ({
    id: `${client.name}-${client.clientNumber}`,
    rank: index + 1,
    name: client.name,
    currentBalance: client.currentBalance || 0,
    totalPerformance: client.totalPerformance || 0,
    nOfDeposits: client.depositCount ?? 0,
    totalDeposits: client.totalDeposits || 0,
    nOfWithdrawals: client.withdrawalCount ?? 0,
    totalWithdrawals: client.totalWithdrawals || 0,
    clientsOnlyFund: (client.totalDeposits || 0) - (client.totalWithdrawals || 0),
    monthsActive: client.monthsActive || 0,
    accountOpenDate: client.accountOpenDate || null
  })), [allClientsSorted]);

  const rows = useMemo(() => {
    const list = includeZeroBalance ? allRows : allRows.filter((r) => r.currentBalance !== 0 && r.currentBalance !== null);
    return list.map((r, i) => ({ ...r, rank: i + 1 }));
  }, [allRows, includeZeroBalance]);

  const summary = useMemo(() => ({
    totalClients: rows.length,
    totalBalance: rows.reduce((s, r) => s + (r.currentBalance || 0), 0),
    totalDeposits: rows.reduce((s, r) => s + (r.totalDeposits || 0), 0),
    totalWithdrawals: rows.reduce((s, r) => s + (r.totalWithdrawals || 0), 0),
    totalClientOnlyFund: rows.reduce((s, r) => s + (r.totalDeposits || 0) - (r.totalWithdrawals || 0), 0)
  }), [rows]);

  if (isLoading || !clientData) {
    return <Skeleton height={400} style={{ borderRadius: '8px' }} />;
  }

  return (
    <Box>
      <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
        Source: live from Firestore — <code>users</code> (role=1), <code>ledgers</code>, <code>transection_requests</code>. N D = approved deposits, N W = paid withdrawals. Account Open Date = first ledger in DB (contract dates to be added in restoration). No script required; refresh the page to update.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={includeZeroBalance}
              onChange={(e) => setIncludeZeroBalance(e.target.checked)}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#2af598' } }}
            />
          }
          label={
            <Typography sx={{ color: '#ccc' }}>
              Include zero balance / closed accounts
            </Typography>
          }
        />
        <Typography variant="caption" sx={{ color: '#888', ml: 1 }}>
          Summary below uses filtered list
        </Typography>
      </Box>
      {/* Summary Stats – from visible (filtered) rows */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
              border: '1px solid rgba(42, 245, 152, 0.2)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: '14px', color: '#ccc', mb: 1 }}>
                Total Individual Clients
              </Typography>
              <Typography sx={{ fontSize: '28px', fontWeight: '700', color: '#fff' }}>
                {summary.totalClients}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>{includeZeroBalance ? 'All (incl. closed/0)' : 'With balance only'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
              border: '1px solid rgba(42, 245, 152, 0.2)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: '14px', color: '#ccc', mb: 1 }}>
                Total Balance
              </Typography>
              <Typography sx={{ fontSize: '28px', fontWeight: '700', color: '#fff' }}>
                {formatCurrency(summary.totalBalance)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>Sum of visible client balances</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
              border: '1px solid rgba(42, 245, 152, 0.2)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: '14px', color: '#ccc', mb: 1 }}>
                Total Deposits
              </Typography>
              <Typography sx={{ fontSize: '28px', fontWeight: '700', color: '#fff' }}>
                {formatCurrency(summary.totalDeposits)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>Sum of visible clients</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
              border: '1px solid rgba(42, 245, 152, 0.2)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: '14px', color: '#ccc', mb: 1 }}>
                Total Withdrawals
              </Typography>
              <Typography sx={{ fontSize: '28px', fontWeight: '700', color: '#fff' }}>
                {formatCurrency(summary.totalWithdrawals)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>Sum of visible clients</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
              border: '1px solid rgba(42, 245, 152, 0.2)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: '14px', color: '#ccc', mb: 1 }}>
                Total Client Only Fund
              </Typography>
              <Typography sx={{ fontSize: '28px', fontWeight: '700', color: '#fff' }}>
                {formatCurrency(summary.totalClientOnlyFund)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>Deposits − withdrawals (visible)</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Grid */}
      <Box
        sx={{
          height: 500,
          width: '100%',
          '& .super-app-theme--header': {
            backgroundColor: 'rgba(42, 245, 152, 0.1)',
            color: '#fff',
            fontWeight: '600'
          },
          '& .MuiDataGrid-root': {
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#fff'
          },
          '& .MuiDataGrid-cell': {
            borderColor: 'rgba(255, 255, 255, 0.1)'
          },
          '& .MuiDataGrid-columnHeaders': {
            borderColor: 'rgba(255, 255, 255, 0.1)'
          },
          '& .MuiDataGrid-footerContainer': {
            borderColor: 'rgba(255, 255, 255, 0.1)',
            color: '#fff'
          },
          '& .MuiTablePagination-root': {
            color: '#fff'
          },
          '& .MuiIconButton-root': {
            color: '#fff'
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(42, 245, 152, 0.05)'
          }
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: 'transparent'
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default ClientAnalytics;

